import SubpageLayout from "@/components/SubpageLayout";
import {
  Smartphone,
  CreditCard,
  Camera,
  Repeat,
  MessageCircle,
  ClipboardList,
  CalendarRange,
  Receipt,
  Users,
} from "lucide-react";

export default function HunddagisPage() {
  return (
    <div data-testid="page-hunddagis">
      <SubpageLayout
        category="Hunddagis-app"
        seoTitle="Hunddagis-app"
        seoDescription="Hundifys hunddagis-app sköter daglig kundkommunikation, bildflöden och återkommande månadsfakturering automatiskt. Få betalt i tid utan Swish-jakten."
        seoPath="/hunddagis-app"
        heroBadge="Hunddagis App"
        title="En app som pratar med hundägarna — och får dem"
        highlightWord="att betala i tid."
        subtitle="Hundifys hunddagis-app sköter daglig kommunikation, bildflöden och återkommande månadsfakturering på autopilot. Mindre tid på admin. Mer tid med hundarna. Och inga obetalda fakturor som ligger och driver."
        heroImage="https://images.pexels.com/photos/37443358/pexels-photo-37443358.jpeg"
        ctaSource="Hunddagis App"
        problem={{
          title: "Kundkommunikation och fakturering är inte ett dagis-jobb.",
          paragraphs: [
            "Du driver ett hunddagis för att du älskar hundar. Inte för att jaga betalningar via Swish-påminnelser, sammanställa månadsräkningar i Excel eller svara på WhatsApp-meddelanden klockan 21:30 om hur Bossen hade det idag.",
            "Men det är just det som äter upp din tid. Och varje gång du missar att skicka en faktura — eller en hundägare missar att Swisha — krymper marginalen. Samtidigt förväntar sig dagens kunder uppdateringar, bilder och tydlighet. Att inte ge det är att förlora dem till nästa dagis.",
            "Hundify samlar det dagliga flödet (incheckning, bilder, anteckningar) och det ekonomiska (autogiro, månadsfaktura, påminnelser) i samma system — så du kan göra det du älskar utan att tappa intäkter.",
          ],
        }}
        workflow={{
          title: "Ett dagis-pass — Hundify-stilen.",
          intro:
            "Hela kedjan från att hunden kliver in till att fakturan dragits, utan att personalen behöver tänka på admin.",
          steps: [
            {
              title: "1. Incheckning på sekunder",
              desc: "Skanna QR-koden vid grinden. Hunden markeras närvarande, ägaren får notis och dagisets dagsschema uppdateras live.",
            },
            {
              title: "2. Bilder & uppdateringar",
              desc: "Personalen tar bilder via appen, taggar hundar och skickar till respektive ägare — utan att kämpa med kontaktlistor.",
            },
            {
              title: "3. Närvarorapport",
              desc: "Vid dagens slut har du en automatisk rapport: antal hundar, avvikelser och vilka som inte hämtats än.",
            },
            {
              title: "4. Återkommande fakturering",
              desc: "Månadsabonnemang debiteras automatiskt via autogiro, Stripe eller Klarna. Påminnelser och inkasso sköts utan att du lyfter en penna.",
            },
          ],
        }}
        stats={[
          { value: "97%", label: "Fakturor betalda i tid" },
          { value: "18 h", label: "Sparad tid / månad" },
          { value: "4.8 ⭐", label: "Genomsnittlig kundnöjdhet" },
        ]}
        features={[
          {
            icon: Smartphone,
            title: "Ägar-app (iOS + Android)",
            desc: "Hundägarna ser dagens schema, bilder, anteckningar och fakturor i en snygg app med ditt varumärke.",
          },
          {
            icon: Camera,
            title: "Bildflöde med taggning",
            desc: "Snabb bildhantering med automatisk taggning av hund. Ägaren får push-notis när nytt foto laddas upp.",
          },
          {
            icon: MessageCircle,
            title: "Tvåvägs-chat",
            desc: "Slut på WhatsApp-kaos. All kommunikation per hund och kund är sökbar och loggad i systemet.",
          },
          {
            icon: Repeat,
            title: "Återkommande månadsfakturering",
            desc: "Sätt upp paket (3/4/5 dagar i veckan). Systemet räknar ut, fakturerar och drar pengar varje månad.",
          },
          {
            icon: CreditCard,
            title: "Autogiro, kort & Klarna",
            desc: "Integrerat med Stripe, Klarna och Bgmax. Slut på Swish-jakten — pengarna kommer in automatiskt.",
          },
          {
            icon: ClipboardList,
            title: "Dagsanteckningar",
            desc: "Personalen loggar humör, måltider, ev. incidenter direkt på telefonen. Ägaren får sammanfattning automatiskt.",
          },
          {
            icon: CalendarRange,
            title: "Närvarokalender",
            desc: "Ägaren bokar extra-dagar eller avbokar via appen. Du ser direkt vad det blir för intäkt.",
          },
          {
            icon: Receipt,
            title: "Automatiska kvitton & SIE-export",
            desc: "Kvitto till kund, bokföringsfil till din revisor — varje månad utan att du gör något.",
          },
          {
            icon: Users,
            title: "Flera dagis-grupper",
            desc: "Dela upp i valpgrupp, seniorgrupp, lekgrupp. Egna scheman och egna närvarolistor per grupp.",
          },
        ]}
        faq={[
          {
            q: "Kan ägarna boka extra-dagar själva i appen?",
            a: "Ja, och du sätter regler: hur sent kan de boka, vilka dagar är fullbokade, vilka grupper de tillhör.",
          },
          {
            q: "Vad händer om en betalning misslyckas?",
            a: "Hundify försöker automatiskt igen, skickar påminnelser och eskalerar till inkasso vid behov — allt utan att du behöver hantera det manuellt.",
          },
          {
            q: "Kan jag använda mitt eget varumärke i appen?",
            a: "Ja. Logga, färger och välkomstskärm anpassas till ditt dagis. Pro-paketet inkluderar full white-label.",
          },
        ]}
      />
    </div>
  );
}
